import { type GroupBase, type Pagination, type PaginationData, createGroupBase } from 'models';
import { useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import type { GroupsListFilter } from '../models';
import { getGroups } from '../services';
import { getGroupsInfiniteQueryKey } from './keys';

type P = {
  filter?: GroupsListFilter;
  pagination: Omit<Pagination, 'position'>;
};

export const useGroupsInfiniteQuery = ({ filter, pagination }: P) =>
  useInfiniteQuery<
    PaginationData<GroupBase>,
    string | undefined,
    ApiClientResponseError<DefaultResponseErrorDataObject>
  >({
    async queryFn({ pageParam: cursor }) {
      const { data: groups } = await getGroups({
        /* eslint-disable @typescript-eslint/naming-convention -- Start: required by backend API */
        config: {
          params: {
            ancestor_id: filter?.ancestorId,
            cursor,
            group_type: filter?.groupType,
            limit: pagination.pageSize,
          },
          /* eslint-enable @typescript-eslint/naming-convention -- End: required by backend API */
        },
      });

      return {
        data: groups.data.map((group) => createGroupBase(group)),
        total: groups.total,
      };
    },
    initialPageParam: undefined,
    getNextPageParam(lastPage, allPages) {
      if (allPages.length * pagination.pageSize >= lastPage.total) {
        return undefined;
      }

      return lastPage.data.at(-1)?.id;
    },
    queryKey: getGroupsInfiniteQueryKey({ filter, pagination }),
  });
